// GlobalChatNotifications — Badge de mensajes nuevos en toda la app
// ----------------------------------------------------------------------------
// Qué hace:
//   - Descubre las salas del usuario (/salas/usuario).
//   - Abre un WebSocket por cada sala: ws://.../chat/ws/{idsala}?token=...
//   - Si llega un evento tipo "message" o "adjunto" desde otra sala o la página
//     /mensajes no está activa, incrementa el contador persistente en
//     localStorage.chat_notificaciones[idsala].
//   - Emite el evento window "chat_noti_update" para que el badge (MensajesNavLink)
//     y otras partes se sincronicen al instante. También reacciona el otro lado
//     al evento "storage" entre pestañas.
//
// Coordinación con otras piezas:
//   - useChat.js guarda chat_current_sala y chat_on_page para que sepamos si el
//     nuevo mensaje corresponde a otra sala o a la misma que el usuario está viendo.
//   - MensajesNavLink combina localStorage con /chat/notificaciones/total para
//     mostrar un número robusto incluso si no hay sockets activos.
//
// Notas:
//   - Si el autor del mensaje soy yo, no se suma notificación.
//   - Los sockets se limpian al desmontar el componente.
//   - Ante errores de red no se lanza excepción; se falla silenciosamente para
//     no afectar a la navegación.
// ----------------------------------------------------------------------------


import { useEffect, useRef } from "react";

const WS_URL = process.env.REACT_APP_WS_URL || (() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    // Fallback al puerto 8000 si no se define la variable de entorno
    return `${proto}//${window.location.hostname}:8000`;
})();

export default function GlobalChatNotifications() {
    const socketRef = useRef(null);

    useEffect(() => {
        let tokenRaw = localStorage.getItem("token") || "";
        if (!tokenRaw) {
            socketRef.current?.close();
            return;
        }

        const token = tokenRaw.replace(/^Bearer\s+/i, "");

        // usa un único WebSocket GLOBAL para todas las notificaciones
        let ws;
        try {
            ws = new WebSocket(`${WS_URL}/chat/ws/global?token=${encodeURIComponent(token)}`);
            socketRef.current = ws;
        } catch (e) {
            return;
        }

        ws.onmessage = (evt) => {
            let p;
            try {
                p = JSON.parse(evt.data);
            } catch {
                return;
            }

            if (!(p?.tipo === "message" || p?.tipo === "adjunto")) return;

            // id de la sala que originó el mensaje
            const payloadSalaId = Number(p.idsala ?? p.sala_id ?? p.sala);
            if (!payloadSalaId) return;

            // Lee info del usuario actual para no auto-notificarse
            let myName = "";
            let myEmail = "";
            try {
                const u = JSON.parse(localStorage.getItem("user") || "null");
                myName = u?.nombre || u?.username || "";
                myEmail = u?.email || "";
            } catch {}
            if (!myName) myName = localStorage.getItem("user_nombre") || "";
            if (!myEmail) myEmail = localStorage.getItem("user_email") || "";

            const autor = p.usuario || p.user_name || p.user || p.user_email || "";
            const isMine = autor === myName || autor === myEmail;

            // ¿Estamos en la página de mensajes?
            const onMensajes = localStorage.getItem("chat_on_page") === "1";
            const currentSalaId = Number(localStorage.getItem("chat_current_sala") || "0");
            const isOtherSala = payloadSalaId !== currentSalaId;

            // Solo sumar si no soy yo Y (no estoy en mensajes O estoy en otra sala)
            if (!isMine && (!onMensajes || isOtherSala)) {
                try {
                    const raw = localStorage.getItem("chat_notificaciones") || "{}";
                    const notis = JSON.parse(raw);
                    notis[payloadSalaId] = (notis[payloadSalaId] || 0) + 1;
                    localStorage.setItem("chat_notificaciones", JSON.stringify(notis));
                    window.dispatchEvent(new Event("chat_noti_update"));
                } catch {}
            }
        };

        ws.onclose = () => {
            socketRef.current = null;
        };

        return () => {
            if (socketRef.current) {
                try {
                    socketRef.current.close(1000, "bye");
                } catch {}
                socketRef.current = null;
            }
        };
    }, []);

    return null;
}